import { z } from 'zod';

// ── Response schemas ────────────────────────────────────────────────

/**
 * Base shopping list schema returned by detail endpoints.
 * `createdBy` is nullable because the originating user may have been deleted.
 */
export const shoppingListSchema = z.object({
  id: z.string().uuid(),
  householdId: z.string().uuid(),
  name: z.string(),
  isActive: z.boolean(),
  isPinned: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Summary shape for the list endpoint.
 * Includes aggregate counts but does NOT include the items array.
 */
export const shoppingListSummarySchema = shoppingListSchema.extend({
  itemCount: z.number().int(),
  checkedCount: z.number().int(),
});

/**
 * A single item within a shopping list.
 * `displayName` is a computed field: customName || product name || 'Unnamed Item'.
 */
export const shoppingListItemSchema = z.object({
  id: z.string().uuid(),
  shoppingListId: z.string().uuid(),
  productId: z.string().uuid().nullable(),
  customName: z.string().nullable(),
  displayName: z.string(),
  quantity: z.number(),
  unit: z.string(),
  isChecked: z.boolean(),
  checkedAt: z.coerce.date().nullable(),
  checkedBy: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  sortOrder: z.number().int(),
  productGroupId: z.string().uuid().nullable(),
  productGroup: z
    .object({
      id: z.string().uuid(),
      name: z.string(),
      icon: z.string().nullable(),
      category: z.string().nullable(),
    })
    .nullable(),
  checkedByUser: z
    .object({
      id: z.string().uuid(),
      name: z.string().nullable(),
      avatarUrl: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Detail shape returned by single-list endpoints.
 * Includes the full items array plus aggregate counts.
 */
export const shoppingListWithItemsSchema = shoppingListSchema.extend({
  items: z.array(shoppingListItemSchema),
  itemCount: z.number().int(),
  checkedCount: z.number().int(),
});

// ── Request schemas ─────────────────────────────────────────────────

/**
 * Data required to create a new shopping list.
 *
 * Note: `includeRestock` was removed until FIR-134 implements the restock
 * suggestion logic. Re-add when that feature is built.
 */
export const createShoppingListSchema = z.object({
  name: z.string().min(1).max(100).optional(),
});

/**
 * Data for updating an existing shopping list.
 */
export const updateShoppingListSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  isActive: z.boolean().optional(),
  isPinned: z.boolean().optional(),
});

/**
 * Data for adding an item to a shopping list.
 * Either `customName` or `productId` should be provided.
 */
export const createShoppingListItemSchema = z
  .object({
    customName: z.string().min(1).max(200).optional(),
    productId: z.string().uuid().optional(),
    productGroupId: z.string().uuid().optional(),
    quantity: z.number().positive().finite().optional(),
    unit: z.string().max(20).optional(),
    notes: z.string().max(500).optional(),
  })
  .refine(
    (data) =>
      data.productId !== undefined ||
      data.customName !== undefined ||
      data.productGroupId !== undefined,
    {
      message: 'Either productId, productGroupId, or customName must be provided',
      path: ['customName'],
    }
  );

/**
 * Data for updating a shopping list item.
 * Use `isChecked` to check/uncheck the item.
 */
export const updateShoppingListItemSchema = z.object({
  customName: z.string().min(1).max(200).optional(),
  quantity: z.number().positive().finite().optional(),
  unit: z.string().max(20).optional(),
  isChecked: z.boolean().optional(),
  notes: z.string().max(500).optional(),
});

/**
 * Ordered list of item IDs defining the new sort order.
 */
export const reorderShoppingListItemsSchema = z.object({
  itemIds: z.array(z.string().uuid()),
});

/**
 * Batch action to apply to a shopping list's items.
 * - `uncheck-all`: uncheck all checked items
 * - `delete-checked`: remove all checked items
 * - `clear`: remove all items
 */
export const batchShoppingListItemsSchema = z.object({
  action: z.enum(['uncheck-all', 'delete-checked', 'clear']),
});

/**
 * Response returned by batch item operations.
 */
export const batchResultSchema = z.object({
  affected: z.number().int(),
});

/**
 * Paginated response for the shopping lists endpoint.
 */
export const paginatedShoppingListsSchema = z.object({
  items: z.array(shoppingListSummarySchema),
  total: z.number().int(),
});

// ── Type exports ────────────────────────────────────────────────────

export type ShoppingList = z.infer<typeof shoppingListSchema>;
export type ShoppingListSummary = z.infer<typeof shoppingListSummarySchema>;
export type ShoppingListItem = z.infer<typeof shoppingListItemSchema>;
export type ShoppingListWithItems = z.infer<typeof shoppingListWithItemsSchema>;
export type CreateShoppingListData = z.infer<typeof createShoppingListSchema>;
export type UpdateShoppingListData = z.infer<typeof updateShoppingListSchema>;
export type CreateShoppingListItemData = z.infer<typeof createShoppingListItemSchema>;
export type UpdateShoppingListItemData = z.infer<typeof updateShoppingListItemSchema>;
export type ReorderShoppingListItemsData = z.infer<typeof reorderShoppingListItemsSchema>;
export type BatchShoppingListItemsData = z.infer<typeof batchShoppingListItemsSchema>;
export type BatchResult = z.infer<typeof batchResultSchema>;
export type PaginatedShoppingListsResponse = z.infer<typeof paginatedShoppingListsSchema>;
